import { FC, MutableRefObject, useEffect, useRef, useState, MouseEvent } from 'react';
import dynamic from 'next/dynamic';

import { showLoginModal, showOutOfStockModal } from '@/redux/slices/gui.slice';

import { useAppDispatch, useAppSelector } from '@/redux';
import {
  NS
} from '@/constants';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { Rating, UntypedObject } from '@/types';
import { AttributeStringModel, ItemModel, ProductModel } from '@/models';
import {
  getAttribute,
  getAttributeId,
  getAttributeValue,
  isBoxProduct,
  isItemLightPriceUSA
} from '@/utils/productUtils';
import { getCriticsRatingNotes } from '@/utils/productCardUtils';

import ProductViewLabelBio from '@/components/common/product/ProductViewLabelBio';
import ProductRatingTag from '@/components/common/product/rating/ProductRatingTag';
import ProductCardAttributes from '@/components/listing/ProductCardAttributes';
import AvailabilityIndicator from '@/components/common/AvailabilityIndicator';
import ProductTagList from '@/components/common/badge/ProductTagList';
import WarningInformation from '@/components/common/WarningInformation';
import RestrictedArea from '@/components/common/RestrictedArea';
import ItemSelector from '@/components/common/ItemSelector';
import ProductPrice from '@/components/product/ProductPrice';
import Reassurance from '@/components/product/Reassurance';
import Icon from '@/components/common/iconedLinks/Icon';
import PromoInfoBox from '@/components/product/PromoInfoBox';

import styles from './ProductActions.module.scss';
import { WINEBOX_MAX } from '@/constants/cart';
import { ENVIRONNEMENT, ATTRIBUTES } from '@/constants/product';
import { URLS } from '@/constants/urls';
import { VirtualAssistantButton } from '@/components/virtual-assistant/virtual-assistant-button';
import { startAssistantConversation } from '@/redux/slices/assistant.slice';
import { isFeatureEnabled } from '@/utils/millesimaUtils';

const ProductVintageBox = dynamic(() => import('@/components/product/ProductVintageBox'));
ProductVintageBox.displayName = 'ProductVintageBox';

type ProductActionsProps = {
  product: ProductModel;
  currentItem: ItemModel;
  items: Array<UntypedObject>;
  onItemChange: (item: UntypedObject) => void;
  t: (key: string, opt?: {ns: string}) => string;
  scrollToWineboxPanel?: () => void;
  scrollToAttributes?: () => void;
  addToCartRef?: MutableRefObject<any>;
  quantity: number;
  setQuantity: (qty: number) => void;
  wineBoxPromotePosition?: string;
}

export const ProductActions: FC<ProductActionsProps> = ({
  product,
  currentItem,
  items,
  onItemChange,
  t,
  scrollToAttributes,
  addToCartRef,
  quantity,
  setQuantity
}) => {
  const dispatch = useAppDispatch();
  const loggedUser = useAppSelector(({ auth }) => auth.loggedUser);
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const { isMobileDevice } = useAppSelector(({ gui }) => gui);
  const box = useAppSelector(({ box }) => box);

  const { isDesktop, isMobileOrTablet, isMobile } = useScreenSizeContext();
  const wineboxQty = box.content.nbItem;
  const isAlert = getAttributeId(product, 'alerte_o_n') === '1';

  const isMobileView = isMobileDevice || isMobileOrTablet;
  const promotion: AttributeStringModel = getAttribute(currentItem, ATTRIBUTES.RIBBONAD);
  const infoSiteLabel = getAttributeValue(currentItem, ATTRIBUTES.INFO_SITE);
  const environnement = getAttribute(product, ATTRIBUTES.ENVIRONNEMENT);
  const promoIncentive = getAttribute(currentItem, ATTRIBUTES.PROMO_INCENTIVE);
  const promoIncentiveActive = getAttributeId(currentItem, ATTRIBUTES.PROMO_INCENTIVE, '0') !== '0';

  const isLabelBioDisplayed = Object.values(ENVIRONNEMENT).includes(environnement?.valueId);

  const ratings = getCriticsRatingNotes(product, storeConf) as Rating[];

  const OutOfStockIcon = () => <Icon src={URLS.OUT_OF_STOCK} alt="out of stock" />;

  const productAttributes = {
    classement: getAttributeValue(product, ATTRIBUTES.RATING, ''),
    region: getAttribute(product, ATTRIBUTES.REGION),
    appellation: getAttribute(product, ATTRIBUTES.APPELLATION),
    couleur: getAttributeValue(product, ATTRIBUTES.COLOR, ''),
    typeProduit: getAttribute(product, ATTRIBUTES.TYPE_PRODUIT),
    sousTypeProduit: getAttribute(product, ATTRIBUTES.SOUS_TYPE_PRODUIT),
    mentionAge: getAttribute(product, ATTRIBUTES.MENTION_AGE),
    matierePremiere: getAttribute(product, ATTRIBUTES.MATIERE_PREMIERE),
    origineSpiritueux: getAttribute(product, ATTRIBUTES.ORIGINE_SPIRITUEUX),
    isSpirit: product?.isSpirit,
    storeConf
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const dragStartX = useRef(0);
  const initialScrollLeft = useRef(0);

  const handleMouseDown = (e: MouseEvent) => {
    const container = scrollRef.current;
    if (!container) return;

    setIsDragging(true);
    dragStartX.current = e.pageX - container.offsetLeft;
    initialScrollLeft.current = container.scrollLeft;
  };

  const handleMouseMove = (e: MouseEvent) => {
    const container = scrollRef.current;
    if (!isDragging || !container) return;

    e.preventDefault();
    const currentX = e.pageX - container.offsetLeft;
    const deltaX = currentX - dragStartX.current;
    container.scrollLeft = initialScrollLeft.current - deltaX;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    setIsScrollable(element.scrollWidth > element.clientWidth);
  }, [ratings.length]);

  const tagsClassName = [
    styles.taglist,
    isDragging && isScrollable && styles.scrolling,
    isScrollable && styles.scrollable
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <ProductTagList
        t={t}
        promotion={promotion}
        infoSiteLabel={infoSiteLabel}
        lightPrice={isItemLightPriceUSA(currentItem, storeConf.storeId)}
        className={styles['custo-badge-list']}
      >
        {isLabelBioDisplayed && (
          <ProductViewLabelBio t={t} environnement={environnement} isMobileView={isMobileView} />
        )}
      </ProductTagList>

      <h1 className={styles.title}>{product.productName}</h1>

      <ProductCardAttributes
        {...productAttributes as any}
        index={1}
        customClassName="mb-3"
        scrollToAttributes={scrollToAttributes}
      />
      {ratings.length > 0 &&
        <div
          ref={scrollRef}
          className={tagsClassName}
          data-test-id="taglist"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseUp}
          onMouseUp={handleMouseUp}
        >
          {ratings.map((rating) => (
            <ProductRatingTag key={rating.shortName} rating={rating} />
          ))}
        </div>
      }

      {items?.length > 0 && currentItem && (
        <ProductPrice
          customClass={styles.price}
          currentItem={currentItem}
          product={product}
          isMobile={isMobileView}
        />
      )}

      {product.exclusive_cellar && !loggedUser?.isEc && (
        <RestrictedArea
          showButton={!loggedUser}
          buttonText={t('restricted.connect')}
          onButtonClick={() => dispatch(showLoginModal())}
          isButtonFullWidth
        />
      )}

      {product.display && product.nbItems > 0 ? (
        <>
          <ItemSelector
            t={t}
            product={product}
            currentItem={currentItem}
            onItemChange={onItemChange}
            title={isMobileView ? '' : t('product:itemSelector.title')}
            isVertical
            customClassName="py-4 pt-md-5 pb-md-3"
            selectorType="bloc"
            addToCartRef={addToCartRef}
            quantity={quantity}
            setQuantity={setQuantity}
          />
          {promoIncentiveActive && (
            <PromoInfoBox
              promoId={promoIncentive?.valueId}
              minimumProducts={promoIncentive?.shortValue}
              text={promoIncentive?.value}
              customClassName={styles['promo-info-box']}
            />
          )}
          {currentItem?.stock > 0 && (
            <AvailabilityIndicator
              customClassName={styles.availability}
              t={t}
              product={product}
              currentItem={currentItem}
              locale={storeConf.locale}
              storeId={storeConf.storeId}
              isProductPage={true}
            />
          )}
        </>
      ) : product.productName && (
        <div className="mt-3">
          <WarningInformation CustomIcon={OutOfStockIcon} title={t('product.noStock')}>
            <p
              onClick={() => dispatch(showOutOfStockModal({
                productName: product.productName,
                productPartNumber: product.partnumber
              }))}
              className={styles.warning}
              data-test-id="out-of-stock"
            >
              {t('outOfStock.warning', { ns: NS.PRODUCT })}
            </p>
          </WarningInformation>
        </div>
      )}

      {isBoxProduct(currentItem?.partnumber) && wineboxQty === WINEBOX_MAX && (
        <div className="mt-3">
          <WarningInformation title={t('product.box.boxFull')}>
            <p>{t('errors.errorCodeMessage.1012')}</p>
          </WarningInformation>
        </div>
      )}

      {isFeatureEnabled(storeConf, 'chatbot') && (
        <section className={styles.assistant}>
          <p className={styles['chatbot-title']}>{t('assistant.productPage.title')}</p>
          <p className={styles['sub-title']}>
            {t('assistant.productPage.subTitle')}
          </p>
          <VirtualAssistantButton
            size="sm" onClick={() => dispatch(startAssistantConversation())} label={t('assistant.name')}
          />
        </section>
      )}

      {product.attributes && (
        <>
          <Reassurance
            isDesktop={isDesktop}
            reassurances={storeConf?.reassurances}
            currentItem={currentItem}
            isAlert={isAlert}
          />
          {!isMobileView && (
            <ProductVintageBox product={product} />
          )}
        </>
      )}
    </>
  );
};

export default ProductActions;
